import React from 'react';
import PropTypes from 'prop-types';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import styled from 'styled-components';

import { TextButton } from './Buttons';

import breakpoints from '../styles/breakpoints';

import { Loader } from '../components/Loader';
import { Error } from '../components/Error';

import LargeNumberSearch from '../components/LargeNumberSearch';
import { withAppStorage } from '../utils/appStorage';
import { withRouter } from 'react-router-dom';

// Same query as in LargeNumberSearch.js for cache.
const GET_CAR_INFORMATION = gql`
  query getCarInformation($regNumber: String!) {
    information: getCarInformation(regNumber: $regNumber) {
      model
      modelYear
      fuel
      engine
      wheelSize
      transmissionType
      chassisNumber
      carData: BilkodData {
        variantId: Bilkod
        brandId: BilNr
        modelId: ModellNr
      }
    }
  }
`;

const StyledCarInformation = styled.div`
  color: ${p => p.theme.colors.lightFont};
`;

const NumberSearch = styled.div`
  background-color: ${p => p.theme.colors.lightBg};
  padding: 30px 10px 10px 10px;
  margin-bottom: 40px;

  ${breakpoints.medium`
        padding: 50px 10px 30px 10px;
    `};
`;

const Model = styled.div`
  color: ${p => p.theme.colors.darkFont};
  font-size: 1.5625rem;
  font-weight: 500;
  line-height: 1.6;
  letter-spacing: 0.3px;
  text-align: center;
  margin-bottom: 40px;
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const InfoWrapper = styled.div`
  padding: 0 5px 10px;
  flex: 0 0 100%;

  ${breakpoints.medium`
	flex: 0 0 50%;
  `};
  ${breakpoints.large`
	flex: 0 0 33.33%;
	max-width: 33.33%;
  `};
`;

const Info = styled.div`
  background-color: ${p => p.theme.colors.lightBg};
  font-size: 1.125rem;
  line-height: 1;
  letter-spacing: 0.2px;
  padding: 20px 16px;
`;

const Label = styled.span`
  font-weight: 700;
`;

const Disclaimer = styled.div`
  font-style: italic;
  font-weight: 300;
  line-height: 1.38;
  letter-spacing: 0.2px;
  margin: 0 5px 20px;
`;

function CarInformation({ regNumber, appStorage, history }) {
  if (!regNumber)
    return (
      <NumberSearch>
        <LargeNumberSearch />
      </NumberSearch>
    );

  return (
    <Query query={GET_CAR_INFORMATION} variables={{ regNumber: regNumber }}>
      {({ loading, error, data }) => {
        if (loading) {
          return <Loader />;
        }

        if (error) {
          return <Error error={error} />;
        }

        if (!data.information) {
          return (
            <NumberSearch>
              <LargeNumberSearch />
            </NumberSearch>
          );
        }

        const info = data.information;

        return (
          <StyledCarInformation>
            <Model>
              {info.model} ({regNumber.toUpperCase()}){' '}
              <TextButton
                onClick={e => {
                  e.preventDefault();

                  appStorage.delete('regNumber');
                  history.push('/bildeler/');
                }}
              >
                (Endre kjøretøy)
              </TextButton>
            </Model>

            <Grid>
              {info.modelYear && (
				<InfoWrapper>
					<Info>
						<Label>Årsmodell:</Label> {info.modelYear}
					</Info>
				</InfoWrapper>
              )}
              {info.fuel && (
				<InfoWrapper>
					<Info>
						<Label>Drivstoff:</Label> {info.fuel}
					</Info>
				</InfoWrapper>
              )}
              {info.engine && (
				<InfoWrapper>
					<Info>
						<Label>Motor:</Label> {info.engine}
					</Info>
				</InfoWrapper>
              )}
              {info.wheelSize && (
				<InfoWrapper>
					<Info>
						<Label>Dekkdimensjon:</Label> {info.wheelSize}
					</Info>
				</InfoWrapper>
              )}
              {info.transmissionType && (
				<InfoWrapper>
					<Info>
						<Label>Girkasse:</Label> {info.transmissionType}
					</Info>
				</InfoWrapper>
              )}
              {info.chassisNumber && (
				<InfoWrapper>
					<Info>
						<Label>Chassisnr:</Label> {info.chassisNumber}
					</Info>
				</InfoWrapper>
              )}
            </Grid>

            <Disclaimer>
              Vær obs på at deler som dukker opp når du søker på ditt regnummer
              ikke nødvendigvis passer på din bil.
              <br />
              For at delen skal passe må den ha samme artikkel/orginalt
              delenummer som delen du skal bytte ut.
            </Disclaimer>
          </StyledCarInformation>
        );
      }}
    </Query>
  );
}

CarInformation.propTypes = {
  regNumber: PropTypes.string
};

export default withRouter(withAppStorage(CarInformation));
